<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'

import type { Props } from './types'
import { getPaymentPlan } from './utils'
import type { Typology } from '@/server/api/buyer-front/types'

import TypologyCarrousel from '@/components/PageQuotationPlan/TypologyCarrouselV2/index.vue'
import QuotationPlanFeeSimulator from '@/components/QuotationPlan/FeeSimulatorV2/index.vue'
import TypologySlider from '@/components/PageQuotationPlan/TypologySliderV2/index.vue'
import { getProductTypeLabel } from '@/utils/project'

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  showUnitsLink: false,
  dataAttributes: {
    dataLh: { P: 'unknown', Z: 'unknown' }
  }
})

const sortTypologiesByMinArea = (typologies: Typology[]) => {
  return typologies.sort((typologyA, typologyB) => typologyA.min_area - typologyB.min_area)
}

const filterTypologiesByName = (typologies: Typology[]) => {
  return typologies.filter(typology => typology.name)
}

const initializeTypologies = () => {
  const sortedTypologies = sortTypologiesByMinArea([...props.typologies])
  return filterTypologiesByName(sortedTypologies)
}

const typologies = ref(initializeTypologies())

const selectedTypologyName = ref<string | undefined>(typologies.value[0]?.name)

const fetchPaymentPlan = () => {
  return getPaymentPlan(props.listingId, selectedTypologyName.value, useNuxtApp())
}

const { data: unitsData, pending, error } = await useAsyncData('paymentPlan', fetchPaymentPlan, {
  watch: [selectedTypologyName]
})

function throwError (statusCode: number, statusMessage: string, fatal: boolean = true): never {
  throw createError({ statusCode, statusMessage, fatal })
}

if (error.value || !unitsData.value?.payment_plans_stats?.length) {
  throwError(503, 'Not payment plan found')
}

function findTypologyByName (typologyName: string) {
  return typologies.value.find(typology => typology.name === typologyName)
}

const selectedTypology = computed(() => findTypologyByName(selectedTypologyName.value))

function getQuotationPlans (limit: number = 2) {
  return unitsData.value?.payment_plans_stats?.slice(0, limit)
}

const quotationPlan = computed(() => getQuotationPlans())

const setTypologyName = (currentName: string) => {
  selectedTypologyName.value = currentName
}

const scrollTop = ref(0)
const principalNavbarVisible = ref(true)

function updateNavbarVisibility (currentY: number) {
  principalNavbarVisible.value = currentY < scrollTop.value
  scrollTop.value = currentY
}

const handleTabsPosition = () => updateNavbarVisibility(window.scrollY)

const productType = computed(() => getProductTypeLabel(props.listingType, true, false, t).toLowerCase())

onMounted(() => {
  window.addEventListener('scroll', () => {
    handleTabsPosition()
  })
})

</script>

<script lang="ts">
export default {
  name: 'QuotationPlanTypologies'
}
</script>

<template>
  <section
    class="quotation-plan-typologies relative mt-56 mb-0">
    <header>
      <h3 class="hl-2-sb lg:display-5">
        {{ t('title', { type: productType }) }}
      </h3>

      <p
        v-sanitize="t('disclaimer', { type: productType })"
        class="text-14 mt-16 lg:mt-12 font-semibold" />
    </header>

    <ClientOnly>
      <TypologySlider
        class="sticky top-20 md:top-24 left-0 right-0 z-20 overflow-y-hidden transition-all duration-200 mx-auto py-24 lg:pt-32 lg:pb-0"
        type="sticky"
        :typologies
        :listing-type
        :data-attributes="dataAttributes"
        :current-typology-code="selectedTypologyName"
        @selected-typology-code="setTypologyName" />
    </ClientOnly>

    <div
      class="flex flex-col gap-24 lg:flex-row-reverse h-auto lg:mt-24">
      <TypologyCarrousel
        :key="`typology-carrousel-${selectedTypologyName}`"
        class="flex-1 max-h-[680px]"
        :class="{ 'blur-sm': pending }"
        :data-attributes="{...dataAttributes, dataLh: {...dataAttributes.dataLh, CTI: selectedTypology?.min_unit_code }}"
        :pictures="selectedTypology?.pictures || []"
        :show-units-link="showUnitsLink"
        :typology-name="selectedTypology?.name || ''" />

      <div
        class="flex-1"
        :class="{ 'blur-sm': pending }">
        <div class="quotation-plan-fee-simulator-wrapper flex flex-col gap-8 h-full justify-between">
          <QuotationPlanFeeSimulator
            class="rounded-[13px] lg:py-0 lg:px-0"
            :listing-id
            :unit-code="selectedTypologyName"
            :initial-fee-price="quotationPlan[0]?.initial_fee"
            :separation-price="quotationPlan[0]?.separation"
            :monthly-payment-price="quotationPlan[0]?.monthly_payment"
            :credit-price="quotationPlan[0]?.credit_price"
            :property-price="quotationPlan[0]?.total_to_pay"
            :monthly-payments-number="quotationPlan[0]?.monthly_payments_number"
            :monthly-payments-and-additions="quotationPlan[0]?.monthly_payments_and_additions"
            :data-attributes="{...dataAttributes, dataLh: {...dataAttributes.dataLh, Z: 'pagos_flexibles_sueños_posibles', CTI: selectedTypology?.min_unit_code }}"
            :currency>
            <slot name="under-simulator" />
          </QuotationPlanFeeSimulator>

          <div class="flex flex-col mt-40 lg:mt-0">
            <span class="body-1-sb lg:display-8 text-pine-600">{{ t('bestDeal') }}</span>

            <slot name="contact-form" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="housingCondition == 'VIS' && currency == 'COP'"
      class="vis-disclaimer mt-24">
      <span
        class="body-3-m text-pine-600">{{ t('visDisclaimer') }}</span>
    </div>
  </section>
</template>

<style lang="scss">
@import "@lahaus-roomie/roomie/src/components/RoomieTabs/index";

.lh-roomie-v2 {
  .quotation-plan-typologies {
    header {
      h3 {
        color: #004132;
      }

      p {
        color: #004132;
      }
    }

    &-images {
      @media (width >= 1080px) {
        width: 373px;
      }
    }

    &__form {
      @media (width >= 1024px) {
        min-width: 400px;
      }
    }

    .app-slider {
      &__button {
        @apply absolute;

        &--left {
          left: 0;
        }

        &--right {
          right: 0;
        }
      }
    }

    .app-contact-form {
      &__title,
      &-loading-text,
      &-success-message {
        font-size: 2rem !important;
        line-height: 1.5 !important;
      }

      &-loading,
      &-success-container {
        @apply bg-white;
      }

      &-loading {
        @apply flex flex-col-reverse;
      }

      &-success-icon {
        @apply flex justify-center;
      }

      &-success-message {
        @apply text-center;
      }
    }

    .quotation-plan-fee-simulator-wrapper {
      border-radius: 2.4rem;
      background: #F2F4F8;
      backdrop-filter: blur(0.2rem);
      padding: 3.2rem 2.4rem;

      @media (width >= theme('screens.lg')) {
        padding: 4rem;
      }
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
